/**
 * TSX Code Generator - Orchestrates the conversion of CraftJS JSON → TSX source files
 *
 * Walks the CraftJS node tree, splits top-level children into section files,
 * and inlines nested children within their parent sections.
 */

import { ImageAsset, scanImages, getImageReference } from "./imageScanner";
import {
  generateText,
  generateImage,
  generateButton,
  generateDivider,
  generateVideo,
  generateGoogleMap,
  generateSocialLinks,
  generateContactInfo,
  generateContactForm,
  generateTestimonial,
  generateServiceCard,
  generateDoctorCard,
  generateFeatureCard,
  generatePricingCard,
  generatePromotionBanner,
  generateFloatingCTA,
  generateLineLoginButton,
  generateContainer,
  generateCarouselSection,
  generateHero,
  generateFooter,
  generateNavLogo,
  generateNavMenu,
  generateNavCTA,
  generateNavbarContainer,
  // Interactive (standalone section files)
  generateFAQSection,
  generateNavbarSection,
  generateFlexNavbarSection,
  generateHeroSliderSection,
  generateBeforeAfterSection,
  generateGallerySection,
  generateFilterableFeaturesSection,
} from "./componentGenerators";
import { ThemeSettings } from "../../types";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface CraftNode {
  type: { resolvedName: string };
  props: Record<string, unknown>;
  nodes?: string[];
  linkedNodes?: Record<string, string>;
}

interface CraftContent {
  ROOT: CraftNode;
  [key: string]: CraftNode;
}

export interface GeneratedFiles {
  files: Map<string, string>;
  images: ImageAsset[];
}

// Components that generate standalone section files (with hooks)
const INTERACTIVE_COMPONENTS = new Set([
  "FAQ",
  "Navbar",
  "FlexNavbar",
  "HeroSlider",
  "BeforeAfter",
  "Gallery",
  "FilterableFeatures",
  "Services",
  "Doctors",
]);

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const toPascalCase = (s: string): string => {
  return s
    .replace(/[^a-zA-Z0-9]/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("");
};

const toSectionName = (typeName: string, index: number): string => {
  const base = typeName.replace(/Export$/, "");
  return `${base}Section${index > 0 ? index + 1 : ""}`;
};

/**
 * Recursively generate inline TSX code for a node and its children
 * (used for nodes nested inside a section)
 */
const generateInlineCode = (
  nodeId: string,
  nodes: CraftContent,
  assets: ImageAsset[],
): string => {
  const node = nodes[nodeId];
  if (!node) return "";

  const typeName = node.type?.resolvedName;
  if (!typeName) return "";

  // Sync linked Text/Button node text back to parent props
  const props = { ...(node.props || {}) };
  if (node.linkedNodes) {
    for (const [key, linkedId] of Object.entries(node.linkedNodes)) {
      const ln = nodes[linkedId];
      if (!ln) continue;
      const lt = ln.type?.resolvedName;
      if ((lt === "Text" || lt === "Button") && ln.props?.text !== undefined) {
        if (key === "pricing-plan") props.planName = ln.props.text;
        if (key === "pricing-credit") props.credit = ln.props.text;
        if (key === "pricing-price") props.price = ln.props.text;
        if (key === "pricing-unit") props.unit = ln.props.text;
        if (key === "pricing-note") props.note = ln.props.text;
        if (key === "card-title") props.title = ln.props.text;
        if (key === "card-desc") props.description = ln.props.text;
        if (key === "doctor-name") props.name = ln.props.text;
        if (key === "doctor-specialty") props.specialty = ln.props.text;
        if (key === "doctor-desc") props.description = ln.props.text;
        if (key === "testimonial-quote") props.quote = ln.props.text;
        if (key === "testimonial-author") props.authorName = ln.props.text;
        if (key === "testimonial-title") props.authorTitle = ln.props.text;
        if (key === "footer-name") props.clinicName = ln.props.text;
        if (key === "footer-desc") props.description = ln.props.text;
        if (key === "footer-address") props.address = ln.props.text;
        if (key === "footer-phone") props.phone = ln.props.text;
        if (key === "footer-email") props.email = ln.props.text;
        if (key === "footer-copyright") props.copyright = ln.props.text;
        if (key === "hero-title") props.title = ln.props.text;
        if (key === "hero-subtitle") props.subtitle = ln.props.text;
        if (key === "hero-button") {
          props.buttonText = ln.props.text;
          if (typeof ln.props.link === "string") props.buttonLink = ln.props.link;
          if (typeof ln.props.target === "string") props.buttonTarget = ln.props.target;
          if (typeof ln.props.linkType === "string") props.buttonLinkType = ln.props.linkType;
        }
        if (key === "contact-name") props.clinicName = ln.props.text;
        if (key === "navbar-logo-text") props.logoText = ln.props.text;
        if (key === "navbar-cta") {
          props.ctaText = ln.props.text;
          if (typeof ln.props.link === "string") props.ctaLink = ln.props.link;
          if (typeof ln.props.target === "string") props.ctaTarget = ln.props.target;
          if (typeof ln.props.linkType === "string") props.ctaLinkType = ln.props.linkType;
        }
        if (key === "promo-title") props.title = ln.props.text;
        if (key === "promo-subtitle") props.subtitle = ln.props.text;
        if (key === "pricing-header-service") props.serviceLabel = ln.props.text;
        if (key === "pricing-header-credit") props.serviceCreditLabel = ln.props.text;
        if (key === "pricing-header-baht") props.bahtLabel = ln.props.text;
        if (key.startsWith("nav-item-") && Array.isArray(props.navItems)) {
          const idx = parseInt(key.replace("nav-item-", ""), 10);
          if (!isNaN(idx) && (props.navItems as Array<Record<string, unknown>>)[idx]) {
            props.navItems = [...(props.navItems as Array<Record<string, unknown>>)];
            (props.navItems as Array<Record<string, unknown>>)[idx] = {
              ...(props.navItems as Array<Record<string, unknown>>)[idx],
              label: ln.props.text,
            };
          }
        }
      }
    }
  }

  // Get children code recursively
  // Components with isCanvas + canMoveIn:false use <Element> linked nodes internally — skip them
  const linkedNodeComponents = new Set(["Hero", "Navbar", "Footer", "ContactInfo", "ServiceCard", "DoctorCard", "FeatureCard", "PricingCard", "Testimonial", "PromotionBanner"]);
  const skipLinkedNodes = linkedNodeComponents.has(typeName);

  const childIds = [...(node.nodes || [])];
  if (!skipLinkedNodes && node.linkedNodes) {
    for (const linkedId of Object.values(node.linkedNodes)) {
      childIds.push(linkedId);
    }
  }
  const childrenCode = childIds
    .map((id) => generateInlineCode(id, nodes, assets))
    .filter(Boolean)
    .join("\n");

  // Generate code based on component type
  switch (typeName) {
    case "Text":
      return generateText(props);
    case "Image":
      return generateImage(props, assets);
    case "Button":
      return generateButton(props);
    case "Divider":
      return generateDivider(props);
    case "Video":
      return generateVideo(props);
    case "GoogleMap":
      return generateGoogleMap(props);
    case "SocialLinks":
      return generateSocialLinks(props);
    case "ContactInfo":
      return generateContactInfo(props);
    case "ContactForm":
      return generateContactForm(props);
    case "Testimonial":
      return generateTestimonial(props, assets, childrenCode);
    case "ServiceCard":
      return generateServiceCard(props, assets, childrenCode);
    case "DoctorCard":
      return generateDoctorCard(props, assets, childrenCode);
    case "FeatureCard":
      return generateFeatureCard(props, assets, childrenCode);
    case "PricingCard":
      return generatePricingCard(props, childrenCode);
    case "PromotionBanner":
      return generatePromotionBanner(props, assets, childrenCode);
    case "FloatingCTA":
      return generateFloatingCTA(props);
    case "LineLoginButton":
      return generateLineLoginButton(props);
    case "Hero":
      return generateHero(props, assets, childrenCode);
    case "Footer":
      return generateFooter(props, assets, childrenCode);
    case "Container":
    case "RootContainer":
      return generateContainer(props, childrenCode, assets);
    // Nav sub-components (inline)
    case "NavLogo":
      return generateNavLogo(props, assets);
    case "NavMenu":
      return generateNavMenu(props);
    case "NavCTA":
      return generateNavCTA(props);
    case "NavbarContainer":
      return generateNavbarContainer(props, childrenCode);
    // Interactive components appearing inline — render as component reference
    // The actual section file is generated separately and imported by the parent
    case "FAQ":
    case "Navbar":
    case "FlexNavbar":
    case "HeroSlider":
    case "BeforeAfter":
    case "Gallery":
    case "FilterableFeatures":
    case "Services":
    case "Doctors":
      return `<${typeName}Section__NESTED />`;
    default:
      // Unknown component - render children in a div
      if (childrenCode) {
        return `<div>${childrenCode}</div>`;
      }
      return `{/* Unknown component: ${typeName} */}`;
  }
};

/**
 * Find nested interactive components in a section subtree.
 * Returns array of { nodeId, typeName } for each found.
 */
const findNestedInteractive = (
  nodeId: string,
  nodes: CraftContent,
): { nodeId: string; typeName: string }[] => {
  const result: { nodeId: string; typeName: string }[] = [];
  const node = nodes[nodeId];
  if (!node) return result;
  const typeName = node.type?.resolvedName;
  if (typeName && INTERACTIVE_COMPONENTS.has(typeName)) {
    result.push({ nodeId, typeName });
    return result; // Don't recurse into interactive component children
  }
  for (const childId of node.nodes || []) {
    result.push(...findNestedInteractive(childId, nodes));
  }
  if (node.linkedNodes) {
    for (const linkedId of Object.values(node.linkedNodes)) {
      result.push(...findNestedInteractive(linkedId, nodes));
    }
  }
  return result;
};

/**
 * Check if a section subtree contains any Button nodes
 */
const sectionHasButtons = (nodeId: string, nodes: CraftContent): boolean => {
  const node = nodes[nodeId];
  if (!node) return false;
  if (node.type?.resolvedName === "Button") return true;
  for (const childId of node.nodes || []) {
    if (sectionHasButtons(childId, nodes)) return true;
  }
  if (node.linkedNodes) {
    for (const linkedId of Object.values(node.linkedNodes)) {
      if (sectionHasButtons(linkedId, nodes)) return true;
    }
  }
  return false;
};

/**
 * Generate a section file for a non-interactive top-level component
 * Wraps the inline-generated code in a simple React.FC component
 */
const generateSimpleSectionFile = (
  sectionName: string,
  inlineCode: string,
  assets: ImageAsset[],
  nodeId: string,
  nodes: CraftContent,
): string => {
  // Collect image imports for this section
  const sectionAssetImports = collectSectionImageImports(nodeId, nodes, assets);

  // Ensure we have valid JSX to return
  const code = inlineCode.trim() || "<></>";

  const hasButtons = sectionHasButtons(nodeId, nodes);

  if (hasButtons) {
    return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React from "react";
${sectionAssetImports}
interface ${sectionName}Props {
  lang?: "th" | "en";
  onNavigate?: (path: string) => void;
}

const ${sectionName}: React.FC<${sectionName}Props> = ({ lang = "th", onNavigate }) => {
  return (
    ${code}
  );
};

export default ${sectionName};
`;
  }

  return `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React from "react";
${sectionAssetImports}
const ${sectionName}: React.FC<{ lang?: "th" | "en" }> = ({ lang = "th" }) => {
  return (
    ${code}
  );
};

export default ${sectionName};
`;
};

/**
 * Collect import statements for images used within a section subtree
 */
const collectSectionImageImports = (
  nodeId: string,
  nodes: CraftContent,
  assets: ImageAsset[],
): string => {
  const usedImports = new Set<string>();

  const walk = (id: string) => {
    const node = nodes[id];
    if (!node?.props) return;

    // Check all string props for image references
    for (const [, value] of Object.entries(node.props)) {
      if (typeof value === "string" && value) {
        const ref = getImageReference(value, assets);
        if (ref.type === "import") {
          const asset = assets.find((a) => a.importName === ref.value);
          if (asset) {
            usedImports.add(`import ${asset.importName} from "../assets/${asset.filename}";`);
          }
        }
      }
      // Check arrays
      if (Array.isArray(value)) {
        for (const item of value) {
          if (item && typeof item === "object") {
            for (const [, v] of Object.entries(item as Record<string, unknown>)) {
              if (typeof v === "string" && v) {
                const ref = getImageReference(v, assets);
                if (ref.type === "import") {
                  const asset = assets.find((a) => a.importName === ref.value);
                  if (asset) {
                    usedImports.add(`import ${asset.importName} from "../assets/${asset.filename}";`);
                  }
                }
              }
            }
          }
        }
      }
    }

    // Walk children
    for (const childId of node.nodes || []) {
      walk(childId);
    }
    if (node.linkedNodes) {
      for (const linkedId of Object.values(node.linkedNodes)) {
        walk(linkedId);
      }
    }
  };

  walk(nodeId);
  return usedImports.size > 0 ? Array.from(usedImports).join("\n") + "\n" : "";
};

// ─────────────────────────────────────────────
// Main Generator
// ─────────────────────────────────────────────

/**
 * Generate all TSX files from CraftJS content
 */
export const generateTsxFiles = (
  content: string | CraftContent,
  slug: string,
  theme: ThemeSettings,
): GeneratedFiles => {
  // Parse content
  const nodes: CraftContent = typeof content === "string" ? JSON.parse(content) : content;
  const rootNode = nodes.ROOT;
  if (!rootNode) {
    throw new Error("No ROOT node found in CraftJS content");
  }

  // Scan for images
  const images = scanImages(nodes);

  // Files to output
  const files = new Map<string, string>();

  // Track section names for deduplication
  const sectionNameCounts = new Map<string, number>();
  const getSectionName = (typeName: string): string => {
    const count = sectionNameCounts.get(typeName) || 0;
    sectionNameCounts.set(typeName, count + 1);
    return toSectionName(typeName, count);
  };

  // Process top-level children of ROOT
  const topLevelIds = rootNode.nodes || [];
  const sectionImports: string[] = [];
  const sectionUsages: string[] = [];
  let hasNavbar = false;
  let hasButtonSections = false;

  for (const childId of topLevelIds) {
    const childNode = nodes[childId];
    if (!childNode) continue;

    const typeName = childNode.type?.resolvedName;
    if (!typeName) continue;

    const sectionName = getSectionName(typeName);
    const props = childNode.props || {};

    if (INTERACTIVE_COMPONENTS.has(typeName)) {
      // Generate standalone section file with hooks
      let sectionCode: string;

      switch (typeName) {
        case "FAQ":
          sectionCode = generateFAQSection(props, sectionName);
          break;
        case "Navbar": {
          // Sync linked Text node text back to navItems labels (user may have edited inline or set multilingual)
          const navProps = { ...props };
          if (childNode.linkedNodes && Array.isArray(navProps.navItems)) {
            navProps.navItems = (navProps.navItems as Array<{ label: unknown; href: string }>).map((item, i) => {
              const linkedId = childNode.linkedNodes?.[`nav-item-${i}`];
              if (linkedId && nodes[linkedId]) {
                const linkedText = nodes[linkedId].props?.text;
                if (linkedText !== undefined) {
                  return { ...item, label: linkedText };
                }
              }
              return item;
            });
          }
          sectionCode = generateNavbarSection(navProps, sectionName, images);
          break;
        }
        case "FlexNavbar": {
          // Get children code for FlexNavbar
          const childIds = [...(childNode.nodes || [])];
          if (childNode.linkedNodes) {
            for (const linkedId of Object.values(childNode.linkedNodes)) {
              childIds.push(linkedId);
            }
          }
          const childrenCode = childIds
            .map((id) => generateInlineCode(id, nodes, images))
            .filter(Boolean)
            .join("\n        ");
          sectionCode = generateFlexNavbarSection(props, childrenCode, sectionName);
          // Inject image imports for children
          const flexNavImports = collectSectionImageImports(childId, nodes, images);
          if (flexNavImports) {
            sectionCode = sectionCode.replace(
              /^(\/\* eslint-disable[^\n]*\*\/\n)?import React/m,
              `$1${flexNavImports}import React`,
            );
          }
          break;
        }
        case "HeroSlider":
          sectionCode = generateHeroSliderSection(props, sectionName, images);
          break;
        case "BeforeAfter":
          sectionCode = generateBeforeAfterSection(props, sectionName, images);
          break;
        case "Gallery":
          sectionCode = generateGallerySection(props, sectionName, images);
          break;
        case "Services":
        case "Doctors": {
          // Collect children (ServiceCard/DoctorCard nodes)
          const carouselChildIds = [...(childNode.nodes || [])];
          if (childNode.linkedNodes) {
            for (const linkedId of Object.values(childNode.linkedNodes)) {
              carouselChildIds.push(linkedId);
            }
          }
          const carouselChildrenCode = carouselChildIds
            .map((id) => generateInlineCode(id, nodes, images))
            .filter(Boolean)
            .join("\n          ");
          const carouselType = typeName === "Services" ? "services" : "doctors";
          sectionCode = generateCarouselSection(carouselType, props, carouselChildrenCode, sectionName);
          // Inject image imports for child cards
          const carouselImports = collectSectionImageImports(childId, nodes, images);
          if (carouselImports) {
            sectionCode = sectionCode.replace(
              /^(\/\* eslint-disable[^\n]*\*\/\n)?import React/m,
              `$1${carouselImports}import React`,
            );
          }
          break;
        }
        case "FilterableFeatures": {
          const childIds = [...(childNode.nodes || [])];
          if (childNode.linkedNodes) {
            for (const linkedId of Object.values(childNode.linkedNodes)) {
              childIds.push(linkedId);
            }
          }
          // Generate card array items: { category: "...", element: <div>...</div> }
          const cardItems = childIds
            .map((id) => {
              const cNode = nodes[id];
              if (!cNode) return "";
              const code = generateInlineCode(id, nodes, images);
              if (!code) return "";
              const childCat = String(cNode.props?.category || "");
              return `{ category: "${childCat.replace(/"/g, '\\"')}", element: (\n      ${code}\n      ) }`;
            })
            .filter(Boolean)
            .join(",\n    ");
          const childrenCode = `[\n    ${cardItems}\n  ]`;
          sectionCode = generateFilterableFeaturesSection(props, childrenCode, sectionName);
          // Inject image imports for children (e.g. FeatureCard icons)
          const filterImports = collectSectionImageImports(childId, nodes, images);
          if (filterImports) {
            sectionCode = sectionCode.replace(
              /^(\/\* eslint-disable[^\n]*\*\/\n)?import React/m,
              `$1${filterImports}import React`,
            );
          }
          break;
        }
        default:
          continue;
      }

      files.set(`sections/${sectionName}.tsx`, sectionCode);
      sectionImports.push(`import ${sectionName} from "./sections/${sectionName}";`);
      if (typeName === "Navbar" || typeName === "FlexNavbar") {
        hasNavbar = true;
        sectionUsages.push(`      <${sectionName} onNavigate={onNavigate} onLangChange={onLangChange} />`);
      } else {
        sectionUsages.push(`      <${sectionName} />`);
      }
    } else {
      // Check for nested interactive components (e.g. Services inside a Container)
      const nestedInteractive = findNestedInteractive(childId, nodes);
      const nestedSectionNames: { placeholder: string; sectionName: string }[] = [];

      for (const ni of nestedInteractive) {
        const niNode = nodes[ni.nodeId];
        if (!niNode) continue;
        const niProps = niNode.props || {};
        const niSectionName = getSectionName(ni.typeName);
        let niSectionCode: string;

        switch (ni.typeName) {
          case "Services":
          case "Doctors": {
            const niChildIds = [...(niNode.nodes || [])];
            if (niNode.linkedNodes) {
              for (const linkedId of Object.values(niNode.linkedNodes)) {
                niChildIds.push(linkedId);
              }
            }
            const niChildrenCode = niChildIds
              .map((id) => generateInlineCode(id, nodes, images))
              .filter(Boolean)
              .join("\n          ");
            const niCarouselType = ni.typeName === "Services" ? "services" : "doctors";
            niSectionCode = generateCarouselSection(niCarouselType, niProps, niChildrenCode, niSectionName);
            const niImports = collectSectionImageImports(ni.nodeId, nodes, images);
            if (niImports) {
              niSectionCode = niSectionCode.replace(
                /^(\/\* eslint-disable[^\n]*\*\/\n)?import React/m,
                `$1${niImports}import React`,
              );
            }
            break;
          }
          default:
            continue;
        }

        files.set(`sections/${niSectionName}.tsx`, niSectionCode);
        nestedSectionNames.push({
          placeholder: `${ni.typeName}Section__NESTED`,
          sectionName: niSectionName,
        });
      }

      // Generate inline code (nested interactive components become <XSection__NESTED /> placeholders)
      let inlineCode = generateInlineCode(childId, nodes, images);

      // Replace placeholders with actual section component names
      for (const ns of nestedSectionNames) {
        inlineCode = inlineCode.replace(
          new RegExp(`<${ns.placeholder}\\s*/>`, "g"),
          `<${ns.sectionName} />`,
        );
      }

      let sectionCode = generateSimpleSectionFile(sectionName, inlineCode, images, childId, nodes);

      // Inject imports for nested interactive sections
      if (nestedSectionNames.length > 0) {
        const nestedImports = nestedSectionNames
          .map((ns) => `import ${ns.sectionName} from "./${ns.sectionName}";`)
          .join("\n");
        sectionCode = sectionCode.replace(
          /^(\/\* eslint-disable[^\n]*\*\/\n(?:import [^\n]+\n)*)import React/m,
          `$1${nestedImports}\nimport React`,
        );
        // Fallback: if the regex didn't match (no other imports), inject after eslint-disable
        if (!sectionCode.includes(nestedImports)) {
          sectionCode = sectionCode.replace(
            `import React from "react";`,
            `${nestedImports}\nimport React from "react";`,
          );
        }
      }

      files.set(`sections/${sectionName}.tsx`, sectionCode);
      sectionImports.push(`import ${sectionName} from "./sections/${sectionName}";`);
      if (sectionHasButtons(childId, nodes)) {
        hasButtonSections = true;
        sectionUsages.push(`      <${sectionName} onNavigate={onNavigate} />`);
      } else {
        sectionUsages.push(`      <${sectionName} />`);
      }
    }
  }

  // Generate index.tsx (main page component)
  const pageName = toPascalCase(slug) || "Page";
  const propsInterface = `
export interface ${pageName}Props {
  lang?: "th" | "en";
  onNavigate?: (path: string) => void;${hasNavbar ? `
  onLangChange?: (lang: string) => void;` : ""}
}
`;
  const componentSignature = `const ${pageName}: React.FC<${pageName}Props> = ({ lang = "th"${hasNavbar || hasButtonSections ? ", onNavigate" : ""}${hasNavbar ? ", onLangChange" : ""} }) => {`;

  const indexContent = `/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState } from "react";
import "./styles.css";
${sectionImports.join("\n")}
${propsInterface}
${componentSignature}
  const [currentLang, setCurrentLang] = useState<"th" | "en">(lang);

  const handleLangChange = (newLang: string) => {
    setCurrentLang(newLang.toLowerCase() as "th" | "en");
    onLangChange?.(newLang);
  };

  return (
    <div className="${slug}-page">
${sectionUsages.map((s) => s.replace(/onLangChange={onLangChange}/g, "onLangChange={handleLangChange}").replace(/\/>$/, "lang={currentLang} />")).join("\n")}
    </div>
  );
};

export default ${pageName};
`;

  files.set("index.tsx", indexContent);

  // Generate styles.css — collect fonts from all nodes
  const extraFonts = collectFonts(nodes);
  const cssContent = generateStylesCss(slug, theme, extraFonts);
  files.set("styles.css", cssContent);

  // Generate README.md
  const readmeContent = generateReadme(slug, pageName, sectionImports.length);
  files.set("README.md", readmeContent);

  return { files, images };
};

// ─────────────────────────────────────────────
// CSS & README generation
// ─────────────────────────────────────────────

/**
 * Collect all unique fontFamily values from the CraftJS node tree
 */
const collectFonts = (nodes: CraftContent): Set<string> => {
  const fonts = new Set<string>();
  for (const node of Object.values(nodes)) {
    const fontFamily = node.props?.fontFamily;
    if (typeof fontFamily === "string" && fontFamily) {
      fonts.add(fontFamily);
    }
  }
  return fonts;
};

const generateStylesCss = (slug: string, theme: ThemeSettings, extraFonts: Set<string> = new Set()): string => {
  const font = theme.font || "Sarabun";
  const fontEN = theme.fontEN || font;
  // Collect all fonts: theme font + EN font + any extra fonts from Text/Button nodes
  const allFonts = new Set<string>([font]);
  if (fontEN !== font) allFonts.add(fontEN);
  extraFonts.forEach((f) => allFonts.add(f));
  const fontImports = Array.from(allFonts)
    .map((f) => `@import url('https://fonts.googleapis.com/css2?family=${f.replace(/ /g, "+")}:wght@300;400;500;600;700&display=swap');`)
    .join("\n");

  return `${fontImports}

.${slug}-page {
  --color-primary: ${theme.primary};
  --color-secondary: ${theme.secondary};
  --font-family: '${font}', sans-serif;
  --font-family-en: '${fontEN}', sans-serif;
  font-family: var(--font-family);
  color: #1f2937;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.${slug}-page *,
.${slug}-page *::before,
.${slug}-page *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

.${slug}-page h1, .${slug}-page h2, .${slug}-page h3,
.${slug}-page h4, .${slug}-page h5, .${slug}-page h6,
.${slug}-page p, .${slug}-page span, .${slug}-page a {
  font-family: inherit;
}

.${slug}-page img {
  max-width: 100%;
}

.${slug}-page a {
  color: inherit;
}

/* Hide scrollbar for carousels */
.${slug}-page div[style*="overflow-x"]::-webkit-scrollbar {
  display: none;
}

/* Responsive: hide on desktop */
@media (min-width: 768px) {
  .${slug}-page [data-hide-on-desktop="true"] {
    display: none !important;
  }
}

/* ===== MOBILE (< 768px) ===== */
@media (max-width: 767px) {
  .${slug}-page [data-hide-on-mobile="true"] {
    display: none !important;
  }

  /* --- Navbar --- */
  .${slug}-page .mobile-menu-btn {
    display: flex !important;
  }
  .${slug}-page .desktop-nav {
    display: none !important;
  }

  /* --- ALL sections: reduce side padding from 60px to 16px --- */
  .${slug}-page > div[style*="60px"] {
    padding: 24px 16px !important;
  }

  /* --- Per-element mobile padding (from mobilePaddingX/Y props) --- */
  .${slug}-page .mobile-pad {
    padding: var(--mobile-pad) !important;
  }

  /* --- Per-element mobile font size (from mobileFontSize prop) --- */
  .${slug}-page .mobile-fs {
    font-size: var(--m-fs) !important;
  }

  /* --- Row layouts: stack vertically on mobile --- */
  .${slug}-page > div[style*="flex-direction: row"] {
    flex-direction: column !important;
  }
  .${slug}-page > div[style*="flex-direction: row"] > div[style*="width:"] {
    width: 100% !important;
  }

  /* --- Font sizes: scale down for mobile --- */
  .${slug}-page h1 {
    font-size: 2.5rem !important;
  }
  .${slug}-page h2 {
    font-size: 1.75rem !important;
  }
  .${slug}-page h3 {
    font-size: 1.5rem !important;
  }
  .${slug}-page span[style*="font-size: 48px"] {
    font-size: 32px !important;
  }
  .${slug}-page span[style*="font-size: 36px"] {
    font-size: 26px !important;
  }
  .${slug}-page span[style*="font-size: 32px"] {
    font-size: 22px !important;
  }
  .${slug}-page p[style*="font-size: 20px"] {
    font-size: 18px !important;
  }

  /* Text row containers: allow flex items to wrap to new lines */
  .${slug}-page div[style*="align-items: baseline"] {
    flex-wrap: wrap !important;
  }

  /* Keep spans intact (nowrap) inside text rows — let flex-wrap handle line breaks */
  .${slug}-page div[style*="align-items: baseline"] > span {
    white-space: nowrap !important;
  }

  /* --- Center text on mobile for hero section only --- */
  .${slug}-page > div[style*="min-height"] h1,
  .${slug}-page > div[style*="min-height"] h3,
  .${slug}-page > div[style*="min-height"] p,
  .${slug}-page > div[style*="min-height"] span {
    text-align: center !important;
  }
  .${slug}-page > div[style*="min-height"] > div {
    align-items: center !important;
  }
  .${slug}-page > div[style*="min-height"] > div > div[style*="flex-direction: row"] {
    justify-content: center !important;
    flex-wrap: wrap !important;
  }

  /* --- Hero buttons: wrap on narrow screens --- */
  .${slug}-page div[style*="min-height"] > div[style*="z-index: 1"] > div[style*="gap"] {
    flex-wrap: wrap !important;
  }
  .${slug}-page div[style*="min-height"] > div[style*="z-index: 1"] a {
    padding: 8px 20px !important;
    font-size: 0.875rem !important;
  }

  /* --- Carousel: 1 card on mobile --- */
  .${slug}-page div[style*="--card-width"] > div {
    flex: 0 0 80% !important;
    min-width: 80% !important;
  }
  .${slug}-page div[style*="--card-width"] > div img {
    height: 140px !important;
  }
  .${slug}-page div[style*="--card-width"] > div h4 {
    font-size: 1rem !important;
  }
  .${slug}-page div[style*="--card-width"] > div p {
    font-size: 0.8125rem !important;
  }

  /* Carousel arrows: smaller on mobile */
  .${slug}-page button[style*="position: absolute"] {
    width: 32px !important;
    height: 32px !important;
  }
  .${slug}-page button[style*="position: absolute"] svg {
    width: 18px !important;
    height: 18px !important;
  }

  /* --- Grid layouts: stack on mobile --- */
  .${slug}-page footer > div > div[style*="grid-template-columns"] {
    grid-template-columns: 1fr !important;
  }
  .${slug}-page div[style*="grid-template-columns: repeat(3"] {
    grid-template-columns: 1fr !important;
  }

  /* --- Feature grid: reduce padding on mobile --- */
  .${slug}-page div[style*="grid-template-columns"] > div[style*="min-height"] {
    padding: 16px !important;
    min-height: 190px !important;
  }

  /* --- Sections that go full width on mobile --- */
  .mobile-full-width {
    width: 100% !important;
    max-width: 100% !important;
  }

  /* --- Row containers: stack vertically on mobile --- */
  .mobile-stack {
    flex-direction: column !important;
  }
  .mobile-stack > div[style*="width:"] {
    width: 100% !important;
    max-width: 100% !important;
  }
}

/* ===== TABLET (768px - 1023px) ===== */
@media (min-width: 768px) and (max-width: 1023px) {
  .${slug}-page div[style*="--card-width"] {
    --card-width: calc((100% - 48px) / 3) !important;
  }
}
`;
};

const generateReadme = (slug: string, pageName: string, sectionCount: number): string => {
  return `# ${pageName}

Exported from ClinicCMS as React components.

## Usage

1. Copy this entire folder into your React project's \`src/\` directory
2. Import and use the component:

\`\`\`tsx
import ${pageName} from "./${pageName}";

function App() {
  return <${pageName} />;
}
\`\`\`

## Structure

- \`index.tsx\` - Main page component
- \`styles.css\` - Scoped styles, CSS variables, responsive rules
- \`sections/\` - Individual section components (${sectionCount} sections)
- \`assets/\` - Local images

## Customization

### Colors
Edit CSS variables in \`styles.css\`:
\`\`\`css
.${slug}-page {
  --color-primary: #3b82f6;
  --color-secondary: #10b981;
}
\`\`\`

### Fonts
Change the Google Fonts import and \`--font-family\` in \`styles.css\`.

## Requirements

- React 18+
- TypeScript (optional but recommended)
- No additional dependencies required

Generated on: ${new Date().toISOString()}
`;
};
